import { useEffect, useRef } from "react";
import { AlertTriangle, CheckCircle2, XCircle, Scale, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { num, eur, INPUT, THEAD, TBODY, TFOOT } from "@/components/memo/PrevisionnelGrids";
import { fetchPassifAuto } from "@/lib/previsionnels";

const YEARS = 10;

function normalize(value) {
    const src = (value?.lignes || []).slice(0, YEARS);
    const lignes = Array.from({ length: YEARS }, (_, i) => ({ taux: src[i]?.taux ?? "", caf: src[i]?.caf ?? "" }));
    return { passif_admis: value?.passif_admis ?? "", lignes };
}

/** LOT 36 — Simulateur d'apurement du passif sur 10 ans (Plan de Continuation). Stocké dans previsionnel_data.plan_10_ans. */
export default function Plan10AnsGrid({ value, onChange, disabled, dossierId }) {
    const plan = normalize(value);
    const passif = num(plan.passif_admis);
    const setLine = (i, patch) => { const lignes = plan.lignes.map((l, j) => (j === i ? { ...l, ...patch } : l)); onChange({ ...plan, lignes }); };

    // LOT 42 — Pré-remplissage auto du Passif Admis depuis la balance (comptes 16/40/42/43/44 + 51 si découvert) si le champ est vide.
    const planRef = useRef(plan); planRef.current = plan;
    const onChangeRef = useRef(onChange); onChangeRef.current = onChange;
    const autoDone = useRef(false);
    useEffect(() => {
        if (!dossierId || autoDone.current) return;
        autoDone.current = true;
        if (String(planRef.current.passif_admis ?? "").trim() !== "") return;
        fetchPassifAuto(dossierId).then((r) => {
            if (!r || r.passif_admis == null || r.passif_admis <= 0) return;
            if (String(planRef.current.passif_admis ?? "").trim() !== "") return;
            onChangeRef.current({ ...planRef.current, passif_admis: String(Math.round(r.passif_admis)) });
            toast.success("Passif Admis pré-rempli", { description: `Somme des comptes 16/40/42/43/44 (+51 si découvert) — exercice ${r.annee}.` });
        }).catch(() => {});
    }, [dossierId]);

    // Solver de lissage : plancher légal 5 % Années 3→9 (borné par la CAF), puis solde résiduel concentré sur l'année 10 (effet « balloon »).
    function autoSolve() {
        if (passif <= 0) { toast.error("Passif Admis requis", { description: "Renseignez le Passif Admis Total avant le lissage." }); return; }
        const n = 10;
        const cap = plan.lignes.map((l) => { const c = num(l.caf); return c > 0 ? (c / passif) * 100 : Infinity; });
        const taux = new Array(n).fill(0);
        for (let i = 2; i < 10; i++) taux[i] = Math.min(5, cap[i]); // HOTFIX LOT 44 : plancher 5 % FORCÉ Années 3→10 (borné CAF)
        let remaining = 100 - taux.reduce((a, b) => a + b, 0);
        const balloon = Math.min(remaining, cap[9] - taux[9]); // LOT 40 : le gros du solde se reporte sur l'année 10
        taux[9] += balloon; remaining -= balloon;
        for (let iter = 0; iter < 80 && remaining > 1e-6; iter++) { // reliquat non plaçable sur Y10 → réparti vers les années tardives, plafonné à la CAF
            const open = [];
            for (let i = 0; i < 9; i++) if (taux[i] < cap[i] - 1e-9) open.push(i);
            if (!open.length) break;
            const wsum = open.reduce((a, i) => a + (i + 1), 0);
            let allocated = 0;
            for (const i of open) {
                const add = Math.min((remaining * (i + 1)) / wsum, cap[i] - taux[i]);
                taux[i] += add; allocated += add;
            }
            remaining -= allocated;
            if (allocated < 1e-9) break;
        }
        if (remaining > 1e-6) taux[n - 1] += remaining; // ultime reliquat → année 10
        const rounded = taux.map((t) => Math.round(t * 100) / 100);
        const diff = Math.round((100 - rounded.reduce((a, b) => a + b, 0)) * 100) / 100;
        rounded[n - 1] = Math.round((rounded[n - 1] + diff) * 100) / 100;
        onChange({ ...plan, lignes: plan.lignes.map((l, i) => ({ ...l, taux: String(rounded[i]) })) });
        toast.success("Répartition lissée sur 10 ans", { description: "Plancher 5 % Années 3→9, solde concentré sur l'Année 10, annuités ≤ CAF, total 100 %." });
    }

    const totalTaux = plan.lignes.reduce((s, l) => s + num(l.taux), 0);
    const totalOk = Math.abs(totalTaux - 100) < 0.01;
    const anneesSous5 = plan.lignes.map((l, i) => ({ i, taux: num(l.taux), set: String(l.taux ?? "").trim() !== "" })).filter((x) => x.i >= 2 && x.set && x.taux < 5).map((x) => x.i + 1);

    return (
        <div data-testid="plan-10-ans" className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1.5">
                    <label className="text-sm text-muted-foreground" htmlFor="plan-passif-admis">Passif Admis Total (€)</label>
                    <Input id="plan-passif-admis" data-testid="plan-passif-admis" inputMode="decimal" value={plan.passif_admis} disabled={disabled} onChange={(e) => onChange({ ...plan, passif_admis: e.target.value })} className={`${INPUT} h-10 w-56 text-right`} placeholder="0" />
                </div>
                <p className="text-xs text-muted-foreground pb-2">Annuité = Passif Admis × Taux. Viabilité : CAF prévisionnelle ≥ Annuité.</p>
                <Button type="button" data-testid="plan-auto-solve" onClick={autoSolve} disabled={disabled} className="ml-auto rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"><Wand2 className="h-4 w-4" /> Auto-répartition viable</Button>
            </div>
            <div className="relative overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className={THEAD}><th className="py-2.5 px-2">Année</th><th className="py-2.5 px-2">Taux d&apos;amortissement (%)</th><th className="py-2.5 px-2 text-right">Annuité (€)</th><th className="py-2.5 px-2">CAF prévisionnelle (€)</th><th className="py-2.5 px-2 text-center">Viabilité</th></tr></thead>
                    <tbody className={TBODY}>
                        {plan.lignes.map((l, i) => {
                            const annuite = passif * num(l.taux) / 100;
                            const cafSet = String(l.caf ?? "").trim() !== "";
                            const ok = num(l.caf) >= annuite;
                            return (
                                <tr key={`plan-year-${i}`} data-testid={`plan-row-${i}`}>
                                    <td className="py-1.5 px-2 font-medium">Année {i + 1}</td>
                                    <td className="py-1.5 px-2"><Input data-testid={`plan-taux-${i}`} inputMode="decimal" value={l.taux} disabled={disabled} onChange={(e) => setLine(i, { taux: e.target.value })} className={`${INPUT} w-28 text-right`} placeholder="0" /></td>
                                    <td data-testid={`plan-annuite-${i}`} className="py-1.5 px-2 text-right tabular-nums text-slate-700">{eur(annuite)}</td>
                                    <td className="py-1.5 px-2"><Input data-testid={`plan-caf-${i}`} inputMode="decimal" value={l.caf} disabled={disabled} onChange={(e) => setLine(i, { caf: e.target.value })} className={`${INPUT} w-32 text-right`} placeholder="0" /></td>
                                    <td className="py-1.5 px-2 text-center">
                                        {!cafSet ? <span className="text-xs text-slate-400">—</span> : ok
                                            ? <span data-testid={`plan-viability-${i}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> OK</span>
                                            : <span data-testid={`plan-viability-${i}`} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700"><XCircle className="h-3.5 w-3.5" /> RISQUE</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot className={TFOOT}><tr><td className="py-2 px-2">Total des taux</td><td data-testid="plan-total-taux" className={`py-2 px-2 text-right ${totalOk ? "text-emerald-700" : "text-rose-700"}`}>{totalTaux.toLocaleString("fr-FR")} %{totalOk ? " ✓" : " / 100 %"}</td><td colSpan={3} className="py-2 px-2 text-xs font-normal text-muted-foreground">{totalOk ? "Plan d'apurement complet (100 %)." : "Le cumul des taux doit atteindre 100 % sur les 10 ans."}</td></tr></tfoot>
                </table>
            </div>
            {anneesSous5.length > 0 && (
                <div data-testid="plan-taux-alert" className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                    <AlertTriangle className="h-5 w-5 shrink-0 text-rose-700" />
                    <span>Contrainte légale : à compter de la 3ᵉ année, chaque annuité doit représenter au moins 5 % du passif. Taux inférieur à 5 % en année {anneesSous5.join(", ")}.</span>
                </div>
            )}
            {!totalOk && (
                <p data-testid="plan-total-alert" className="flex items-center gap-2 text-xs text-amber-700"><Scale className="h-4 w-4" /> Le total des taux d&apos;apurement est de {totalTaux.toLocaleString("fr-FR")} % (objectif : 100 %).</p>
            )}
        </div>
    );
}
